import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { CodeModel } from 'src/app/models/code.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { debounceTime, filter, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-code-feature-access-control',
  templateUrl: './code-feature-access-control.component.html',
  styleUrls: ['./code-feature-access-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CodeFeatureAccessControlComponent implements OnInit, OnDestroy {
  @Input('code') code: CodeModel;

  @Input('code_hash') code_hash: string;

  signInFormData: {
    company_name: string;
    contact_person: string;
    email: string;
    mobile: string;
    mobile_e164: string;
    mobile_country_code: string;
    mobile_dial_code: string;
    reason: string;
    acknowledged: boolean;
  } = {
    company_name: '',
    contact_person: '',
    email: '',
    mobile: '',
    mobile_e164: '',
    mobile_country_code: '',
    mobile_dial_code: '',
    reason: '',
    acknowledged: false
  };

  signOutFormData: {
    id: number;
    company_name: string;
    contact_person: string;
    email: string;
    mobile: string;
  } = {
    id: 0,
    company_name: '',
    contact_person: '',
    email: '',
    mobile: ''
  };

  inProgress: boolean = false;

  // FormControls for autocomplete fields: replaces [(ngModel)] + (input) pattern
  // to avoid mobile keyboard race condition with two-way binding.
  companyNameControl = new FormControl('');
  signInEmailControl = new FormControl('');
  signOutEmailControl = new FormControl('');

  autoCompletedEntityNames: string[] = [];

  /**
   * Whether the current company list is a "did you mean" fallback rather than real matches (SC-2507).
   *
   * The API sets is_suggestion when the search found nothing and fell back to near matches. This is a site
   * entrance sign in, so presenting a guess as a match is how somebody signs in under the wrong company.
   */
  entityNameSuggestionMode: boolean = false;

  /** A company search has completed for the name currently in the field and found nothing. */
  entityNameSearched: boolean = false;

  /**
   * Every company name the search has returned during this sign in, lowercased (SC-2511).
   *
   * Deliberately SEPARATE from autoCompletedEntityNames, and deliberately not cleared alongside it.
   * autoCompletedEntityNames is the visible dropdown, and it is emptied both when the field loses focus
   * (onFieldBlur) and when a name is chosen. Checking "did the user pick a real company" against that list
   * would therefore fail in the exact case it needs to succeed: somebody who selected their employer and then
   * moved to the next field would be asked to confirm creating the company they had just chosen.
   *
   * Accumulates rather than resets per search. A name the API returned once is an account that exists, and
   * that does not stop being true when the user edits the box.
   */
  private knownAccountNames: Set<string> = new Set<string>();

  /**
   * The company name the user has explicitly agreed to have created (SC-2511).
   *
   * Holds the name rather than a boolean so editing the field after agreeing asks again.
   */
  private confirmedNewCompanyName: string | null = null;

  autoCompletedUserEmails: string[] = [];
  autoCompletedOnsiteUserEmails: string[] = [];
  autoCompletedUserObjs: any[] = [];
  autoCompletedOnsiteUserObjs: any[] = [];
  allowAutoComplete: boolean = true; // Only used for Sign-in Email field

  // Mobile field Country Code selector values
  mobile_input_id: string = 'phone-1';
  mobile_number: string = '';
  mobile_country_code: string = '';
  mobile_dial_code: string = '';
  mobile_e164: string = '';
  mobile_error_state: boolean = true;

  private destroy$ = new Subject<void>();

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private router: Router,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (!this.code || !this.code.site_id) {
      this.utils.showToast(
        'This is an invalid QR code or it is not associated with a ' +
          this.utils
            .getLangTerm('parent-child-sites-combined.singular', 'Site')
            .toLowerCase() +
          '. You will be redirected shortly.'
      );
      setTimeout(() => {
        this.router.navigate(['sign-in']);
      }, 5000);
      return;
    }

    this.setupAutocompleteSubscriptions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up reactive subscriptions for all three autocomplete fields.
   * Uses valueChanges + debounceTime to decouple typing from API calls.
   * API calls run outside Angular zone; results are applied via requestAnimationFrame
   * to avoid interrupting the mobile keyboard's input buffer with DOM updates.
   */
  private setupAutocompleteSubscriptions() {
    // Header to skip the global loading spinner for autocomplete requests.
    // The spinner's show/hide causes DOM manipulation that interrupts mobile keyboard input.
    const silentHeaders = { 'X-Skip-Spinner': 'true' };

    // Company Name autocomplete
    this.companyNameControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(450),
        // Any edit invalidates a previous agreement to create this company (SC-2511). Done in a tap rather
        // than in the subscribe below, because the subscribe only runs for terms of 3 characters or more and
        // deleting back to two characters still counts as changing your mind.
        tap(() => {
          this.confirmedNewCompanyName = null;
        }),
        filter((value: string) => value && value.length >= 3)
      )
      .subscribe((value: string) => {
        this.zone.runOutsideAngular(() => {
          this.api
            .makeRequest(
              'get',
              'v2/global-account-search',
              {},
              {
                search: value,
                order_by: 'name',
                order_direction: 'asc'
              },
              silentHeaders
            )
            .then((response) => {
              const names = response.data.map(
                (record: { name: any }) => record.name
              );
              const isSuggestion = Boolean(response.is_suggestion);
              requestAnimationFrame(() => {
                this.zone.run(() => {
                  // Drop a response that arrived after the user carried on typing, so the list and the
                  // "not found" note always describe what is actually in the field.
                  if ((this.companyNameControl.value || '') !== value) {
                    return;
                  }
                  this.autoCompletedEntityNames = names;
                  // Remember these as accounts that exist, independently of the dropdown's lifetime.
                  names.forEach((name: string) =>
                    this.knownAccountNames.add((name || '').trim().toLowerCase())
                  );
                  // Set inside the same zone.run as the list, deliberately. Assigning outside it would
                  // leave the flags and the list disagreeing until the next unrelated change detection
                  // pass, and updating the DOM outside this sequence is what interrupts the mobile
                  // keyboard's input buffer. That is the whole reason this block exists.
                  this.entityNameSuggestionMode = isSuggestion;
                  this.entityNameSearched = true;
                  this.cdr.detectChanges();
                });
              });
            });
        });
      });

    // Sign-in Email autocomplete
    this.signInEmailControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(450),
        filter(
          (value: string) =>
            value && value.length >= 3 && this.allowAutoComplete
        )
      )
      .subscribe((value: string) => {
        this.zone.runOutsideAngular(() => {
          this.api
            .makeRequest(
              'get',
              'v1/tablet/signed-in-users-search',
              {},
              {
                search: value,
                site_id: this.code.site_id,
                code_hash: this.code_hash,
                only_onsite_users: false,
                sign_in_field: 'email',
                order_by: 'email',
                order_direction: 'asc'
              },
              silentHeaders
            )
            .then((response) => {
              const objs = response.data;
              const emails = [
                ...new Set(
                  response.data.map((record: { email: any }) => record.email)
                )
              ] as string[];
              requestAnimationFrame(() => {
                this.zone.run(() => {
                  this.autoCompletedUserObjs = objs;
                  this.autoCompletedUserEmails = emails;
                  this.cdr.detectChanges();
                });
              });
            });
        });
      });

    // Sign-out Email autocomplete
    this.signOutEmailControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(450),
        filter((value: string) => value && value.length >= 3)
      )
      .subscribe((value: string) => {
        this.zone.runOutsideAngular(() => {
          this.api
            .makeRequest(
              'get',
              'v1/tablet/signed-in-users-search',
              {},
              {
                search: value,
                site_id: this.code.site_id,
                code_hash: this.code_hash,
                only_onsite_users: true,
                sign_in_field: 'email',
                order_by: 'email',
                order_direction: 'asc'
              },
              silentHeaders
            )
            .then((response) => {
              const objs = response.data;
              const emails = [
                ...new Set(
                  response.data.map((record: { email: any }) => record.email)
                )
              ] as string[];
              requestAnimationFrame(() => {
                this.zone.run(() => {
                  this.autoCompletedOnsiteUserObjs = objs;
                  this.autoCompletedOnsiteUserEmails = emails;
                  this.cdr.detectChanges();
                });
              });
            });
        });
      });
  }

  /**
   * Get Output values from phone input component.
   */
  getOutputPhoneDetails($event) {
    if ($event) {
      let phone = $event;
      this.mobile_number =
        phone.hasOwnProperty('number') && phone.number !== null
          ? phone.number
          : '';
      this.mobile_country_code =
        phone.hasOwnProperty('countryCode') && phone.countryCode !== null
          ? phone.countryCode
          : '';
      this.mobile_dial_code =
        phone.hasOwnProperty('dialCode') && phone.dialCode !== null
          ? phone.dialCode
          : '';
      this.mobile_e164 =
        phone.hasOwnProperty('e164Number') && phone.e164Number !== null
          ? phone.e164Number
          : '';
      this.mobile_error_state =
        phone.hasOwnProperty('errorState') && phone.errorState !== null
          ? phone.errorState
          : true;
    }
  }

  /**
   * Set the selected entity name and clear the autocomplete results.
   * @param name The selected entity name.
   */
  onSelectEntityName(name: string): void {
    this.companyNameControl.setValue(name, { emitEvent: false });
    this.signInFormData.company_name = name;
    this.autoCompletedEntityNames = [];
    // Choosing from the list is the clearest possible statement that this company already exists, so record
    // it (SC-2511). Needed as well as the search-response capture because setValue uses emitEvent false, so
    // no new search runs to re-record it, and the dropdown is cleared on the next line.
    this.knownAccountNames.add((name || '').trim().toLowerCase());
    // The name is now a known account, so nothing is being created and the "not found" note must go.
    this.entityNameSearched = false;
    this.entityNameSuggestionMode = false;
    this.confirmedNewCompanyName = null;
  }

  /**
   * Whether the company name in the field is one the search has returned during this sign in.
   *
   * Compared case-insensitively and ignoring surrounding whitespace, because somebody who picks a suggestion
   * and then adjusts its capitalisation has still chosen that company.
   */
  private entityNameMatchesAKnownAccount(): boolean {
    const typed: string = (this.companyNameControl.value || '').trim().toLowerCase();

    if (!typed) {
      return false;
    }

    return this.knownAccountNames.has(typed);
  }

  /**
   * Clear autocomplete dropdown on blur (with delay to allow mousedown selection).
   * @param field The autocomplete field to clear.
   */
  onFieldBlur(field: string): void {
    setTimeout(() => {
      if (field === 'entityNames') {
        this.autoCompletedEntityNames = [];
      } else if (field === 'userEmails') {
        this.autoCompletedUserEmails = [];
      } else if (field === 'onsiteUserEmails') {
        this.autoCompletedOnsiteUserEmails = [];
      }
    }, 200);
  }

  /**
   * Set the selected user email.
   * Clear the list of auto completed user emails.
   * Autofill Full Name and Mobile fields.
   * @param email The selected user email.
   */
  onSelectUserEmail(email: string): void {
    // Clear Autocomplete results
    this.autoCompletedUserEmails = [];
    // Update the FormControl without triggering another search
    this.signInEmailControl.setValue(email, { emitEvent: false });
    // Find User Details Object in array with matching email
    let autoCompletedUserObj = this.autoCompletedUserObjs.find(
      (obj) => obj.email == email
    );

    // Autofill Full Name and Mobile fields
    this.signInFormData.contact_person = autoCompletedUserObj.contact_person
      ? autoCompletedUserObj.contact_person
      : '';

    // Set Input values for phone input component.
    this.signInFormData.mobile = autoCompletedUserObj.mobile
      ? autoCompletedUserObj.mobile
      : '';
    this.signInFormData.mobile_e164 = autoCompletedUserObj.mobile_e164
      ? autoCompletedUserObj.mobile_e164
      : '';
    this.signInFormData.mobile_country_code = autoCompletedUserObj.mobile_country_code
      ? autoCompletedUserObj.mobile_country_code
      : '';
    this.signInFormData.mobile_dial_code = autoCompletedUserObj.mobile_dial_code
      ? autoCompletedUserObj.mobile_dial_code
      : '';
  }

  /**
   * Set the selected onsite user email.
   * Clear the list of auto completed onsite user emails.
   * Autofill Full Name, Email and Mobile fields.
   * @param email The selected onsite user email.
   */
  onSelectOnsiteUserEmail(email: string): void {
    // Clear Autocomplete results
    this.autoCompletedOnsiteUserEmails = [];
    // Clear the search field
    this.signOutEmailControl.setValue('', { emitEvent: false });
    // Find User Details Object in array with matching email
    let autoCompletedOnsiteUserObj = this.autoCompletedOnsiteUserObjs.find(
      (obj) => obj.email == email
    );

    // Autofill ID, Company Name, Full Name, Email and Mobile fields
    this.signOutFormData.id = autoCompletedOnsiteUserObj.id
      ? autoCompletedOnsiteUserObj.id
      : 0;
    this.signOutFormData.company_name = autoCompletedOnsiteUserObj.company_name
      ? autoCompletedOnsiteUserObj.company_name
      : '';
    this.signOutFormData.contact_person = autoCompletedOnsiteUserObj.contact_person
      ? autoCompletedOnsiteUserObj.contact_person
      : '';
    this.signOutFormData.mobile = autoCompletedOnsiteUserObj.mobile
      ? autoCompletedOnsiteUserObj.mobile
      : '';
    this.signOutFormData.email = autoCompletedOnsiteUserObj.email
      ? autoCompletedOnsiteUserObj.email
      : '';
  }

  onSignIn(form: NgForm) {
    if (!form.valid) {
      this.utils.showToast(
        'Please enter all required fields denoted with "*".'
      );
      return;
    }

    // Check Error states of phone input then continue submission
    if (this.mobile_error_state) {
      if (this.mobile_number.length < 3) {
        this.utils.showToast(
          'A Phone number must be at least 3 characters long.'
        );
      } else {
        this.utils.showToast(
          'Please enter all required fields denoted with "*".'
        );
      }
      return;
    }

    // Check if another request is already in progress.
    if (this.inProgress) {
      return;
    }

    // Do not create a new company without saying so (SC-2511).
    //
    // Signing in with a company name the search did not return creates a brand new account. That has to stay
    // possible, because somebody arriving at a site whose employer is genuinely not on SiteConnect must be
    // able to sign in. So this ACKNOWLEDGES rather than blocks.
    //
    // What it prevents is the silent case: the company is already here under a slightly different spelling,
    // nobody is told, and the site ends up with two records for one contractor.
    //
    // Placed before inProgress is set so cancelling leaves the form usable.
    const typedCompanyName: string = (this.companyNameControl.value || '').trim();

    if (typedCompanyName && !this.entityNameMatchesAKnownAccount()) {
      if (this.confirmedNewCompanyName !== typedCompanyName) {
        this.utils.showModal(
          'Is this a new company?',
          '<p>We could not find <b>' +
            typedCompanyName +
            '</b> on SiteConnect.</p>' +
            (this.autoCompletedEntityNames.length
              ? '<p>Did you mean one of these?</p><ul><li>' +
                this.autoCompletedEntityNames.slice(0, 5).join('</li><li>') +
                '</li></ul><p>If so, close this and choose it from the list instead.</p>'
              : '') +
            '<p>Continuing will add <b>' +
            typedCompanyName +
            '</b> as a new company.</p>',
          () => {
            // Remember the exact name agreed to, then run the same submit again.
            this.confirmedNewCompanyName = typedCompanyName;
            this.onSignIn(form);
          }
        );
        return;
      }
    }

    // Sync FormControl values into signInFormData before submission
    this.signInFormData.company_name = this.companyNameControl.value || '';
    this.signInFormData.email = this.signInEmailControl.value || '';

    // Update User details with Mobile field Country Code selector values
    this.signInFormData.mobile = this.mobile_number;
    this.signInFormData.mobile_country_code = this.mobile_country_code;
    this.signInFormData.mobile_dial_code = this.mobile_dial_code;
    this.signInFormData.mobile_e164 = this.mobile_e164;

    this.inProgress = true;

    this.api
      .makeRequest(
        'post',
        `v2/codes/signin/${this.code_hash}`,
        this.signInFormData
      )
      .then((response) => {
        this.utils.showModal('Message', response.message);
        this.router.navigate(['sign-in']);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  clearWhiteSpace(content: string) {
    if (content && content != '') {
      return content.replace(/&nbsp;/gi, '');
    }
    return '';
  }

  onSignOut(form: NgForm) {
    if (!form.valid || this.signOutFormData.id <= 0) {
      this.utils.showToast(
        'Please select Email address from Autocomplete list.'
      );
      return;
    }

    this.inProgress = true;

    this.api
      .makeRequest('post', `v2/codes/signout/${this.code_hash}`, {
        user_id: this.signOutFormData.id
      })
      .then((response) => {
        this.utils.showModal('Message', response.message);
        this.router.navigate(['sign-in']);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  onClear() {
    // Clear Autocomplete results
    this.autoCompletedOnsiteUserEmails = [];

    // Clear Form
    this.signOutFormData.id = 0;
    this.signOutFormData.company_name = '';
    this.signOutFormData.contact_person = '';
    this.signOutFormData.mobile = '';
    this.signOutFormData.email = '';

    // Clear search field
    this.signOutEmailControl.setValue('', { emitEvent: false });
  }

  hideEmail(email: string) {
    // e.g. clint.esau@sitesoft.com -> c*********@sitesoft.com
    return email
      ? email.replace(
          /\B.+@/g,
          (c) =>
            c
              .split('')
              .slice(0, -1)
              .map((v) => '*')
              .join('') + '@'
        )
      : email;
  }

  hideMobile(mobile: string) {
    let mask_mobile = mobile;

    if (mobile && mobile.length >= 5) {
      // e.g. 021702780 -> 02*****80
      mask_mobile =
        mobile.substring(0, 2) +
        '*'.repeat(mobile.length - 4) +
        mobile.substring(mobile.length - 2, mobile.length);
    } else if (mobile && mobile.length == 4) {
      // e.g. 3224 -> 3**4
      mask_mobile = mobile.substring(0, 1) + '*'.repeat(2) + mobile.slice(-1);
    } else if (mobile && mobile.length == 3) {
      // e.g. 111 -> 1*1
      mask_mobile = mobile.substring(0, 1) + '*' + mobile.slice(-1);
    }

    return mobile ? mask_mobile : mobile;
  }
}
